import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { UserContext } from "@/components/User";
import {
  deleteJobRequestFilter,
  IJobRequestURLFilter,
  postJobRequestFilter,
  putJobRequestFilter,
} from "@/data/job-requests";
import { useToasts } from "@/views/components/Toast/useToasts";

import { mapToJobRequestFilter, mapToJobRequestFilterDto } from "./mappers";

export function useSavedSearchesActions() {
  const User = useContext(UserContext);
  const companyId = User.contextCompany?.id;

  const { addToast } = useToasts();

  const { t } = useTranslation(["common", "session"]);

  const createSearch = useCallback(
    async (values: IJobRequestURLFilter) => {
      try {
        const newFilter = await postJobRequestFilter(
          { companyId },
          mapToJobRequestFilterDto(mapToJobRequestFilter(values)),
        );

        addToast({
          text: t("session:JOB_REQUESTS.LIST.FILTERS.FORM.MESSAGE_SUCCESS"),
          type: "success",
        });

        return newFilter;
      } catch (e) {
        addToast({ text: t("common:ERROR_MESSAGES.GENERAL"), type: "warning" });
        return undefined;
      }
    },
    [addToast, companyId],
  );

  const updateSearch = useCallback(
    async (values: IJobRequestURLFilter) => {
      try {
        const newFilter = await putJobRequestFilter(
          { id: values.id!, companyId },
          mapToJobRequestFilterDto(mapToJobRequestFilter(values)),
        );

        addToast({
          text: t("session:JOB_REQUESTS.LIST.FILTERS.FORM.SAVE_SUCCESS"),
          type: "success",
        });

        return newFilter;
      } catch {
        addToast({ text: t("common:ERROR_MESSAGES.GENERAL"), type: "warning" });
        return undefined;
      }
    },
    [addToast, companyId],
  );

  const deleteSearch = useCallback(
    async (id: number) => {
      try {
        await deleteJobRequestFilter({ id, companyId });

        addToast({
          text: t("session:JOB_REQUESTS.LIST.FILTERS.MESSAGE_REMOVE_SUCCESS"),
          type: "success",
        });
      } catch {
        addToast({ text: t("common:ERROR_MESSAGES.GENERAL"), type: "warning" });
      }
    },
    [addToast, companyId],
  );

  return {
    createSearch,
    updateSearch,
    deleteSearch,
  };
}
